import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useWeeklyGridSiblings } from './useWeeklyGridSiblings';
import { Loader2, AlertCircle, RefreshCw, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getRounds } from '@/api/tournament/tournamentRoundApi';
import type { TournamentRound } from '@/types/tournament-round.model';
import { RoundsTimeline } from './RoundsTimeline';
import { RoundDetail } from './RoundDetail';
import { handleGlobalError } from '@/utils/error-handler';
import type { Club } from '@/types/tournament.model';

interface RoundsCardProps {
  tournamentId: string;
  /** Main tournament club (for the round schedule editor). */
  club?: Club | null;
  matchDuration?: number;
  sportUid?: string | null;
  /**
   * Signals that the fixture already exists. Rounds are created along with the fixture,
   * so when it flips false->true (fixture just generated in the session) we must
   * re-fetch the rounds: this card is a sibling of FixtureSection and does not
   * notice the generation on its own.
   */
  fixtureReady?: boolean;
  /** Refreshes the tournament (fixture, groups) after a round action (open/propose/publish). */
  onTournamentRefresh?: () => void | Promise<void>;
}

/**
 * Combines a freshly received round with the one already in state. The list endpoint nulls
 * `schedule` (summary view), so a list refresh would otherwise wipe the schedule of a round whose
 * detail was already loaded. The cached schedule is only kept while the status is unchanged: the
 * server rewrites the schedule on status transitions (opening a round builds its week from the
 * weekly grid), and in that case the detail has to be fetched again.
 */
function mergeRound(current: TournamentRound | undefined, incoming: TournamentRound): TournamentRound {
  if (incoming.schedule != null || !current || current.status !== incoming.status) {
    return incoming;
  }
  // listRounds returns schedule and siblingRounds null: keep what the detail already loaded.
  // RoundDetail reloads the detail before the actions that depend on the siblings' state.
  return { ...incoming, schedule: current.schedule, siblingRounds: incoming.siblingRounds ?? current.siblingRounds };
}

/**
 * Round management card for LONG tournaments.
 * Loads the rounds, renders the timeline and the detail of the selected one.
 */
export function RoundsCard({ tournamentId, club, matchDuration, sportUid, fixtureReady, onTournamentRefresh }: RoundsCardProps) {
  const [rounds, setRounds] = useState<TournamentRound[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRoundId, setSelectedRoundId] = useState<string | null>(null);
  // A link from a sibling tournament opens one of this tournament's rounds: /torneos/{id}?jornada={roundId}
  const [searchParams] = useSearchParams();
  const requestedRoundId = searchParams.get('jornada');
  const cardRef = useRef<HTMLDivElement>(null);
  const gridSiblings = useWeeklyGridSiblings(tournamentId, club?.id);

  const fetchRounds = useCallback(async (silent = false) => {
    // silent: refresh after an action. No whole-card spinner, and a failure keeps the current list
    // instead of replacing the whole view with the error card.
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const data = await getRounds(tournamentId);
      setRounds((prev) =>
        data.map((incoming) => mergeRound(prev.find((r) => r.id === incoming.id), incoming)),
      );
      // Select the round the link asked for, else the first active round, else the first one overall
      if (data.length > 0 && !selectedRoundId) {
        const requested = requestedRoundId ? data.find((r) => r.id === requestedRoundId) : undefined;
        const active = data.find((r) =>
          ['CREATED', 'AVAILABILITY_OPEN', 'PROPOSED'].includes(r.status),
        );
        setSelectedRoundId(requested?.id ?? active?.id ?? data[0].id);
      }
    } catch (err) {
      if (!silent) setError('No se pudieron cargar las jornadas');
      handleGlobalError(err, 'Error', 'No se pudieron cargar las jornadas');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [tournamentId, selectedRoundId, requestedRoundId]);

  useEffect(() => {
    fetchRounds();
    // fixtureReady triggers the refetch when the fixture is generated within the session
    // (rounds are born with the fixture); tournamentId covers switching tournaments.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId, fixtureReady]);

  const handleRoundUpdated = useCallback((updatedRound: TournamentRound) => {
    setRounds((prev) =>
      prev.map((r) => (r.id === updatedRound.id ? mergeRound(r, updatedRound) : r)),
    );
  }, []);

  // React to later changes of ?jornada (e.g. a link to another round of this tournament) even when
  // a round is already selected. Each requested id is applied once, so the user can still pick
  // another round while the parameter stays in the URL.
  const appliedRequestRef = useRef<string | null>(null);
  useEffect(() => {
    if (!requestedRoundId || requestedRoundId === appliedRequestRef.current) return;
    if (rounds.some((r) => r.id === requestedRoundId)) {
      appliedRequestRef.current = requestedRoundId;
      setSelectedRoundId(requestedRoundId);
    }
  }, [requestedRoundId, rounds]);

  const selectedRound = rounds.find((r) => r.id === selectedRoundId) ?? null;

  // The rounds card sits below the tournament info: bring it into view when a link opened a round.
  useEffect(() => {
    if (requestedRoundId && selectedRoundId === requestedRoundId) {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [requestedRoundId, selectedRoundId]);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          <span className="ml-2 text-sm text-muted-foreground">Cargando jornadas...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-10">
          <AlertCircle className="h-8 w-8 text-red-500" />
          <p className="text-sm text-red-700">{error}</p>
          <Button variant="outline" size="sm" onClick={() => fetchRounds()}>
            <RefreshCw className="mr-1 h-4 w-4" />
            Reintentar
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card ref={cardRef}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-blue-600" />
          Fechas
        </CardTitle>
        {gridSiblings.length > 0 && (
          <p className="text-sm text-muted-foreground">
            Comparte las canchas de cada semana con{' '}
            {gridSiblings.map((sibling, i) => (
              <span key={sibling.id}>
                {i > 0 && ', '}
                <Link
                  to={`/torneos/${sibling.id}`}
                  className="font-medium text-blue-700 underline-offset-2 hover:underline"
                >
                  {sibling.name}
                </Link>
              </span>
            ))}
            . Las propuestas de la semana se arman juntas.
          </p>
        )}
      </CardHeader>
      <CardContent>
        {rounds.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No hay jornadas todavía. Genera el fixture para crear las fechas.
          </p>
        ) : selectedRound && selectedRoundId ? (
          <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
            {/* Timeline */}
            <div>
              <RoundsTimeline
                rounds={rounds}
                selectedRoundId={selectedRoundId}
                onSelectRound={(id) => setSelectedRoundId(id)}
              />
            </div>

            {/* Selected round detail */}
            <RoundDetail
              tournamentId={tournamentId}
              round={selectedRound}
              club={club}
              matchDuration={matchDuration}
              sportUid={sportUid}
              onBack={() => setSelectedRoundId(null)}
              onRoundUpdated={handleRoundUpdated}
              onAfterAction={async () => {
                // Independent refreshes: running them in parallel shortens the wait after an action.
                await Promise.all([fetchRounds(true), onTournamentRefresh?.()]);
              }}
            />
          </div>
        ) : (
          <RoundsTimeline
            rounds={rounds}
            selectedRoundId={null}
            onSelectRound={(id) => setSelectedRoundId(id)}
          />
        )}
      </CardContent>
    </Card>
  );
}
